import { FC, ReactElement } from 'react'
import { useNavigate } from 'react-router'

import { ProfileListItem } from '@/compositions'
import { useChatConnection } from '@/context/ChatConnectionContext'
import { formatDate } from '@/helpers/date'
import { getUserFullName } from '@/helpers/getUserFullName'
import { hasStreamAccess } from '@/helpers/streamAccess'
import { convertSpacesToDash } from '@/helpers/tools'
import { trans } from '@/helpers/trans'
import { ProfileAvatar } from '@/pages/ProfilePage/subcomponents'
import { useTypedSelector } from '@/redux/store'
import { DateFormat } from '@/types'

import './ProfilePage.scss'

export const ProfilePage: FC = (): ReactElement | null => {
    const navigate = useNavigate()
    const { currentUser } = useTypedSelector(state => state.userReducer)
    const { client: chatClient } = useChatConnection()

    if (! currentUser) {
        return null
    }

    const {
        employeeNumber,
        email,
        phone,
        person,
        nationality,
        transportationOptions,
    } = currentUser

    const { identificationType, isCompanyMedic, emergencyContact } = person
    const address = person.addresses?.at(-1)
    const name = getUserFullName(currentUser)
    const birthday = formatDate(new Date(currentUser.birthday), DateFormat.date)
    const gender = trans(`common.${person.gender || 'unknown'}`)

    const showStreamFeatures = hasStreamAccess(currentUser)

    const onItemClick = (title: string): void => {
        const route = convertSpacesToDash(title)
        navigate(`/${trans('nav.profile.link')}/${route}`)
    }

    return (
        <div className="profile-page">
            {showStreamFeatures && <ProfileAvatar name={name} image={chatClient?.user?.image} />}
            <ul>
                <ProfileListItem
                    key="name"
                    title={trans('pages.profile.name')}
                    values={[name]}
                />
                <ProfileListItem
                    key="employee-number"
                    title={trans('common.employeeNumber')}
                    values={[employeeNumber]}
                />
                <ProfileListItem
                    key="birthday"
                    title={trans('pages.profile.birthday')}
                    values={[birthday]}
                />
                <ProfileListItem
                    key="nationality"
                    title={trans('pages.profile.nationality')}
                    values={[nationality]}
                />
                <ProfileListItem
                    key="isCompanyMedic"
                    title={trans('pages.profile.isCompanyMedic')}
                    values={[trans(`pages.profile.${isCompanyMedic}`)]}
                />
                <ProfileListItem
                    key="identificationType"
                    title={trans('pages.profile.identificationType')}
                    values={[trans(`pages.profile.${identificationType}`)]}
                />
                <ProfileListItem
                    key="password"
                    title={trans('pages.profile.passwordAndPincode')}
                    onClick={onItemClick}
                />
                {showStreamFeatures && (
                    <ProfileListItem
                        key="notificationSettings"
                        title={trans('pages.profile.notificationSettings')}
                        onClick={onItemClick}
                    />
                )}
                <ProfileListItem
                    key="address"
                    title={trans('pages.profile.address')}
                    values={[
                        address?.street,
                        address?.houseNumber,
                        address?.houseNumberAddition,
                        address?.postalCode,
                        address?.city,
                    ]}
                    onClick={onItemClick}
                />
                <ProfileListItem
                    key="email"
                    title={trans('pages.profile.email')}
                    values={[email]}
                    onClick={onItemClick}
                />
                <ProfileListItem
                    key="phone"
                    title={trans('pages.profile.phone')}
                    values={[phone]}
                    onClick={onItemClick}
                />
                <ProfileListItem
                    key="emergencyContact"
                    title={trans('pages.profile.emergencyContact')}
                    values={[
                        emergencyContact?.fullName,
                        emergencyContact?.phone,
                        emergencyContact?.relationType,
                    ]}
                    onClick={onItemClick}
                />
                <ProfileListItem
                    key="gender"
                    title={trans('common.gender')}
                    values={[gender]}
                    onClick={onItemClick}
                />
                <ProfileListItem
                    key="transportationOption"
                    title={trans('pages.profile.transportationOption')}
                    values={[transportationOptions?.[0]?.name || trans('pages.profile.transportationOptionEmpty')]}
                    onClick={onItemClick}
                />
            </ul>
        </div>
    )
}
